import React, { useMemo, useRef, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { db } from '../../firebase/firebase';
import { useDarkMode } from '../../contexts/darkModeContext';
import { archiveOrder, sweepOrders } from './manualArchive';

export default function ManualOrderArchiver({ activeOrders, onClose, onArchived }) {
    const { darkMode } = useDarkMode();
    const previous = new Date();
    previous.setMonth(Math.floor(previous.getMonth() / 3) * 3, 1);
    previous.setMonth(previous.getMonth() - 3);
    const [quarter, setQuarter] = useState(Math.floor(previous.getMonth() / 3) + 1);
    const [year, setYear] = useState(previous.getFullYear());
    const [busy, setBusy] = useState(false);
    const lock = useRef(false);
    const [result, setResult] = useState(null);
    const validYear = Number.isInteger(year) && year >= 1900 && year <= 9999;
    const preview = useMemo(() => sweepOrders(activeOrders, quarter, year), [activeOrders, quarter, year]);
    const total = Object.values(preview).reduce((sum, orders) => sum + orders.length, 0);
    const fieldClass = `w-full rounded border p-2 ${darkMode ? 'bg-darkBgColor border-neutral-600' : 'bg-white border-gray-300'}`;

    const archive = async () => {
        if (lock.current || !validYear || !preview.move.length) return;
        lock.current = true;
        setBusy(true);
        const counts = { moved: 0, missing: 0, skipped: 0, conflict: 0, failed: 0, processed: 0 };
        const removed = [];
        for (const order of preview.move) {
            try {
                const outcome = await archiveOrder(db, order, quarter, year);
                counts[outcome] += 1;
                if (outcome === 'moved' || outcome === 'missing') removed.push(order);
            } catch (error) {
                counts.failed += 1;
                console.error('Manual archive failed for order', order.id, error);
            }
            counts.processed += 1;
            setResult({ ...counts });
        }
        try {
            onArchived(removed);
        } finally {
            lock.current = false;
            setBusy(false);
        }
    };

    return (
        <Dialog open onClose={() => { if (!lock.current) onClose(); }} className="relative z-50">
            <DialogBackdrop className="fixed inset-0 bg-black/60" />
            <div className="fixed inset-0 overflow-y-auto p-4 flex items-center justify-center">
                <DialogPanel className={`w-full max-w-xl rounded-xl p-6 shadow-xl max-h-full overflow-y-auto ${darkMode ? 'bg-darkMainColor text-white' : 'bg-white text-gray-900'}`}>
                    <DialogTitle className="text-lg font-semibold">Manually archive active orders</DialogTitle>
                    <p className="mt-2 text-sm">Only orders in this page’s active order list are included. Select the quarter when the orders were submitted.</p>
                    <div className="grid grid-cols-2 gap-3 my-4">
                        <label>Quarter<select className={fieldClass} value={quarter} disabled={busy} onChange={event => { setQuarter(+event.target.value); setResult(null); }}>
                            {[1, 2, 3, 4].map(value => <option key={value} value={value}>Q{value}</option>)}
                        </select></label>
                        <label>Year<input className={fieldClass} type="number" min="1900" max="9999" value={year || ''} disabled={busy} onChange={event => { setYear(+event.target.value); setResult(null); }} /></label>
                    </div>
                    {!validYear ? <p role="alert">Enter a valid four-digit year.</p> : <>
                        <p className="text-sm mb-3">Destination: <strong>archive_Q{quarter}_{year}</strong>. Dates from {new Date(year, (quarter - 1) * 3, 1).toLocaleDateString()} through {new Date(year, quarter * 3, 0).toLocaleDateString()}, inclusive.</p>
                        <dl className="grid grid-cols-3 gap-3 rounded-lg bg-gray-500/10 p-4 text-center">
                            {[['Scanned', total], ['Will move', preview.move.length], ['Will stay', total - preview.move.length]].map(([label, count]) => <div key={label}><dt className="text-sm">{label}</dt><dd className="text-xl font-semibold">{count}</dd></div>)}
                        </dl>
                        <p className="text-sm my-3">Outside quarter: {preview.outside.length} · Missing/invalid submission time: {preview.invalidDate.length} · Missing order location: {preview.invalidPath.length}</p>
                        <p className="text-sm">Archiving keeps the original document ID and all saved fields, then removes the active copy. Submission times are checked again before each move using your local timezone. Existing archive copies are skipped.</p>
                    </>}
                    {result && <p role="status" className="my-4 text-sm">{busy ? 'Archiving… ' : 'Finished. '}{result.processed} processed: {result.moved} moved, {result.skipped} no longer match, {result.missing} already absent, {result.conflict} already have an archive copy, {result.failed} failed. Failed and skipped orders are not deleted.</p>}
                    <div className="flex justify-end gap-3 mt-5">
                        <button className="rounded border px-4 py-2 disabled:opacity-50" disabled={busy} onClick={onClose}>Close</button>
                        <button className="rounded bg-green-600 text-white px-4 py-2 disabled:opacity-50" disabled={busy || !validYear || !preview.move.length} onClick={archive}>{busy ? 'Archiving…' : `Archive ${preview.move.length} orders`}</button>
                    </div>
                </DialogPanel>
            </div>
        </Dialog>
    );
}
